import React, { type FC, type PropsWithChildren, type ReactNode } from 'react'
import { Box, Stack, type SxProps, type Theme } from '@mui/material'

import { Breadcrumbs } from './Breadcrumbs'
import { SessionBanner } from './SessionBanner'

/**
 * The page sits on a white panel with rounded top corners, laid on the grey frame. The corners are two steps of the
 * theme's corner unit, as `borderRadius: 2` would give, but `sx` only scales `borderRadius` itself, not single corners.
 */
const getPanelRadius = (theme: Theme): number => Number(theme.shape.borderRadius) * 2
const panelStyle: SxProps<Theme> = {
    height: '100%',
    width: 0,
    flexGrow: 1,
    backgroundColor: 'common.white',
    borderTopLeftRadius: getPanelRadius,
    borderTopRightRadius: getPanelRadius,
    overflow: 'hidden',
}
const mainContentStyle: SxProps<Theme> = { p: 6, flexGrow: 1, minHeight: 0, overflowY: 'auto' }

/**
 * `banner` is an optional slot, not a hardcoded import - this component lives under `Navigation/`, a different
 * feature folder. It sits inside the page panel, above the page, so it pushes down only the page content and never
 * the side bar, which `AppNavigation` draws. On phones, the session banner sits above it while the session is near expiration.
 */
export const RouteNavigation: FC<PropsWithChildren<Readonly<{ banner?: ReactNode }>>> = ({ banner, children }) => (
    <Stack sx={panelStyle} data-testid="page-panel">
        <SessionBanner />
        {banner}
        <Box sx={mainContentStyle}>
            <Breadcrumbs />
            {children}
        </Box>
    </Stack>
)
